import { useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { versionService } from '@/services/versionService';
import useAlertStore from '@/stores/alertStore';

interface VersionInfoProps {
  showDetailed?: boolean;
  compact?: boolean;
}

const VersionInfo = ({ compact = false }: VersionInfoProps) => {
  const versionInfo = versionService.getVersionInfo();
  const { showAlert } = useAlertStore();
  const [checking, setChecking] = useState(false);

  const handleForceCheck = async () => {
    setChecking(true);
    try {
      const result = await versionService.forceVersionCheck();
      if (result === 'up_to_date') {
        showAlert("You're on the latest version", 'success');
      } else if (result === 'cache_cleared') {
        showAlert('Cache refreshed with latest updates', 'info');
      }
    } finally {
      setChecking(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (compact) {
    return (
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        v{versionInfo.version}
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" sx={{ mb: 1, color: 'text.secondary', display: 'block' }}>
        <strong>App Version:</strong> {versionInfo.version} • Built: {formatDate(versionInfo.buildTime)}
        {versionInfo.buildNumber && ` • Build #${versionInfo.buildNumber}`}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
        Version information helps us provide better support.
      </Typography>

      <Button
        variant="text"
        size="small"
        startIcon={checking ? <CircularProgress size={14} /> : <RefreshIcon />}
        onClick={handleForceCheck}
        disabled={checking}
        sx={{
          fontSize: '12px',
          textTransform: 'none',
          px: 0,
          minWidth: 'auto',
          color: 'text.secondary',
          '&:hover': {
            backgroundColor: 'transparent',
            color: 'primary.main',
          },
        }}
      >
        {checking ? 'Checking...' : 'Check for updates'}
      </Button>

      {!versionInfo.isLatest && (
        <Typography variant="caption" sx={{ color: 'warning.main', display: 'block', mt: 1 }}>
          ⚠️ Update available - consider refreshing the page
        </Typography>
      )}
    </Box>
  );
};

export default VersionInfo;
